import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import apiService from '../services/apiService';

const AutocompleteContainer = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  width: 400px;
  max-height: 400px;
  background: rgba(20, 20, 20, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  margin-top: 8px;
  overflow: hidden;
  backdrop-filter: blur(10px);
  z-index: 1001;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.7);

  @media (max-width: 768px) {
    width: 320px;
    right: -20px;
  }

  @media (max-width: 480px) {
    width: 280px;
    right: -40px;
  }
`;

const SuggestionsList = styled.div`
  max-height: 350px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 3px;
  }
`;

const SuggestionItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  &:hover,
  &.focused {
    background: rgba(255, 255, 255, 0.1);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const SuggestionPoster = styled.img`
  width: 40px;
  height: 60px;
  object-fit: cover;
  border-radius: 4px;
  margin-right: 12px;
  flex-shrink: 0;
`;

const SuggestionContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const SuggestionTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: white;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SuggestionMeta = styled.div`
  font-size: 0.7rem;
  color: #b3b3b3;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SuggestionYear = styled.span`
  background: rgba(255, 255, 255, 0.2);
  padding: 2px 6px;
  border-radius: 12px;
  font-size: 0.65rem;
`;

const SuggestionRating = styled.span`
  color: #f5c518;
  display: flex;
  align-items: center;
  gap: 2px;

  &::before {
    content: '⭐';
    font-size: 0.6em;
  }
`;

const NoResults = styled.div`
  padding: 20px 16px;
  text-align: center;
  color: #b3b3b3;
  font-size: 0.9rem;
`;

const LoadingSpinner = styled.div`
  padding: 20px 16px;
  text-align: center;
  color: #b3b3b3;
  font-size: 0.9rem;
`;

const SearchAutocomplete = ({ query, isVisible, onSelect, onClose }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const debounceTimeout = useRef(null);

  useEffect(() => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    // Debounce search requests
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    setLoading(true);

    debounceTimeout.current = setTimeout(async () => {
      try {
        const results = await apiService.searchContent(query);
        // Limit to top 8 results for autocomplete
        setSuggestions(results.slice(0, 8));
      } catch (error) {
        console.error('Search suggestions error:', error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [query]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isVisible || suggestions.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex(prev => 
            prev < suggestions.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex(prev => prev > 0 ? prev - 1 : -1);
          break;
        case 'Enter':
          e.preventDefault();
          if (focusedIndex >= 0 && suggestions[focusedIndex]) {
            handleSuggestionClick(suggestions[focusedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, suggestions, focusedIndex, onClose]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible, onClose]);

  const handleSuggestionClick = (suggestion) => {
    onSelect(suggestion);
    navigate(`/title/${suggestion.id}`);
    onClose();
  };

  if (!isVisible || query.length < 2) {
    return null;
  }

  return (
    <AnimatePresence>
      <AutocompleteContainer
        ref={containerRef}
        as={motion.div}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.2 }}
      >
        <SuggestionsList>
          {loading ? (
            <LoadingSpinner>Searching...</LoadingSpinner>
          ) : suggestions.length === 0 ? (
            <NoResults>No results found for "{query}"</NoResults>
          ) : (
            suggestions.map((suggestion, index) => (
              <SuggestionItem
                key={suggestion.id}
                className={index === focusedIndex ? 'focused' : ''}
                onClick={() => handleSuggestionClick(suggestion)}
                onMouseEnter={() => setFocusedIndex(index)}
                onMouseLeave={() => setFocusedIndex(-1)}
              >
                <SuggestionPoster
                  src={suggestion.thumbnailUrl}
                  alt={suggestion.title}
                  onError={(e) => {
                    e.target.src = `https://via.placeholder.com/40x60/333/fff?text=${encodeURIComponent(suggestion.title.charAt(0))}`;
                  }}
                />
                <SuggestionContent>
                  <SuggestionTitle>{suggestion.title}</SuggestionTitle>
                  <SuggestionMeta>
                    {suggestion.year && (
                      <SuggestionYear>{suggestion.year}</SuggestionYear>
                    )}
                    {suggestion.imdbRating && (
                      <SuggestionRating>{suggestion.imdbRating}</SuggestionRating>
                    )}
                    {suggestion.genre && suggestion.genre.length > 0 && (
                      <span>{suggestion.genre[0]}</span>
                    )}
                  </SuggestionMeta>
                </SuggestionContent>
              </SuggestionItem>
            ))
          )}
        </SuggestionsList>
      </AutocompleteContainer>
    </AnimatePresence>
  );
};

export default SearchAutocomplete;